import {
  Platform,
  Pressable,
  type GestureResponderEvent,
  type PressableProps,
  type View,
} from "@-/com.npmjs.react-native";
import { GestureDetector, useLongPressGesture } from "@-/com.npmjs.react-native-gesture-handler";
import useLatest from "@-/com.npmjs.use-latest";
import { createContext, use, useMemo, useRef } from "react";
import { DEFAULT_DELAY_LONG_PRESS_MS } from "#default-delay-long-press-ms";

export type LongPressableProps = Omit<PressableProps, "delayLongPress" | "onLongPress"> &
  React.RefAttributes<View> & {
    /** How long the touch must be held before `onLongPress` fires, in ms. */
    delayLongPress?: number;
    /**
     * Passing this prop at all, even as `undefined`, mounts the native recognizer. `undefined`
     * only disables it, so a call site that toggles it keeps one stable tree.
     */
    onLongPress?: (() => void) | undefined;
  };

type LongPressGesture = ReturnType<typeof useLongPressGesture>;

/**
 * Testing Library cannot reach a handler behind a native gesture, so it reads `testOnly_` props
 * instead, as it does for Gesture Handler's own `Pressable`.
 */
const IS_TEST_ENVIRONMENT = process.env.NODE_ENV === "test";

/** The nearest enclosing `LongPressable`'s recognizer, which an inner one blocks. */
const EnclosingLongPressGestureContext = createContext<LongPressGesture | undefined>(undefined);

/**
 * A `Pressable` whose long press is recognised natively, on the UI thread, in input order.
 *
 * `Pressable`'s own `onLongPress` starts a 500ms JS timer at touch-down and drops `onPress` when
 * the timer fires before the release reaches JS. A UI-thread stall between DOWN and UP (a GC, a
 * slow frame) delays the release past the timer, so a quick tap turns into a long press and does
 * nothing. Gesture Handler times the hold on the UI thread instead: a release already queued
 * behind a stall is handled before the overdue hold timer, so only a real hold activates.
 *
 * The JS touch is left alone, exactly as `Pressable` leaves it: the press stays in until release,
 * and a parent `PanResponder` can still take the touch over (the deck chart drags a selection out
 * of a held bar). The activation reaches JS before its release, so the release's `onPress` is
 * dropped here. Web keeps `Pressable`'s own long press: its timer and its input share one thread.
 * Without an `onLongPress` prop this is a plain `Pressable` and costs nothing extra.
 */
export function LongPressable(props: LongPressableProps) {
  if (Platform.OS === "web" || !("onLongPress" in props)) {
    return <Pressable {...props} />;
  }
  return <NativeLongPressable {...props} />;
}

function NativeLongPressable({
  delayLongPress = DEFAULT_DELAY_LONG_PRESS_MS,
  disabled,
  onLongPress,
  onPress,
  onPressIn,
  onPressOut,
  ...props
}: LongPressableProps) {
  const onLongPressRef = useLatest(onLongPress);
  // Whether this pressable holds the JS press. Native recognizers ignore React Native's responder
  // rules: an enclosing one also times a hold that began on a child, and a parent `PanResponder`
  // can take the touch over while the hold is still timing. Either way this press is not in.
  const isPressedRef = useRef(false);
  // Whether this touch's hold already became a long press, so its release must not also press.
  const longPressedRef = useRef(false);
  const enclosingLongPressGesture = use(EnclosingLongPressGestureContext);
  const isLongPressEnabled = onLongPress !== undefined && disabled !== true;
  const longPressGesture = useLongPressGesture(
    useMemo(
      () => ({
        // The innermost long press wins, as the innermost `Pressable` does: an enclosing one
        // waits for this to fail instead of activating first and cancelling it.
        block: enclosingLongPressGesture,
        cancelsJSResponder: false,
        enabled: isLongPressEnabled,
        minDuration: delayLongPress,
        onActivate: () => {
          if (!isPressedRef.current) {
            return;
          }
          longPressedRef.current = true;
          onLongPressRef.current?.();
        },
        runOnJS: true,
        // Lets tests find the recognizer by its pressable's id (`getByGestureTestId`).
        testID: props.testID,
      }),
      [delayLongPress, enclosingLongPressGesture, isLongPressEnabled, onLongPressRef, props.testID],
    ),
  );

  return (
    <EnclosingLongPressGestureContext value={longPressGesture}>
      <GestureDetector gesture={longPressGesture}>
        <Pressable
          {...props}
          {...(IS_TEST_ENVIRONMENT && isLongPressEnabled
            ? { testOnly_onLongPress: onLongPress }
            : undefined)}
          disabled={disabled}
          onPress={
            onPress == null
              ? onPress
              : (event: GestureResponderEvent) => {
                  if (!longPressedRef.current) {
                    onPress(event);
                  }
                }
          }
          onPressIn={(event: GestureResponderEvent) => {
            isPressedRef.current = true;
            longPressedRef.current = false;
            onPressIn?.(event);
          }}
          onPressOut={(event: GestureResponderEvent) => {
            isPressedRef.current = false;
            onPressOut?.(event);
          }}
        />
      </GestureDetector>
    </EnclosingLongPressGestureContext>
  );
}
